import { onSnapshot, query, where } from "firebase/firestore"
import { getInvoicesCollectionRef } from "./billing"
import type { HourDeficiencyInvoice } from "./billing-types"
import {
  INVOICE_HOUR_DEDUCTION_STATUSES,
  type InvoiceStatus,
} from "./constants/statuses"
import type { FirestoreInstance } from "./types/entities"

const deductionStatuses = new Set<InvoiceStatus>(
  INVOICE_HOUR_DEDUCTION_STATUSES,
)

type InvoiceHourScope = HourDeficiencyInvoice & {
  familyKey?: string
  aggregatePersonIds?: string[]
  aggregateUserIds?: string[]
  totals?: { billableDeficientHours?: number }
}

const invoiceTotalHours = (invoice: InvoiceHourScope): number =>
  invoice.totals?.billableDeficientHours ?? invoice.deficientHours ?? 0

export class AmbiguousLegacyInvoiceError extends Error {
  constructor(
    public readonly invoiceId: string,
    public readonly entityIds: string[],
  ) {
    super(
      `Legacy invoice ${invoiceId} cannot be assigned to one stable family ledger.`,
    )
    this.name = "AmbiguousLegacyInvoiceError"
  }
}

const toDate = (value: unknown): Date | null => {
  if (value instanceof Date) return value
  if (
    typeof value === "object" &&
    value !== null &&
    "toDate" in value &&
    typeof value.toDate === "function"
  ) {
    return value.toDate()
  }
  if (
    typeof value === "object" &&
    value !== null &&
    "seconds" in value &&
    typeof value.seconds === "number"
  ) {
    return new Date(value.seconds * 1000)
  }
  if (typeof value === "string" || typeof value === "number") {
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? null : date
  }
  return null
}

/**
 * Sums non-voided invoice hours for the requested family entities and period.
 * Grouped invoices use their per-entity lines; legacy invoices use the total.
 */
type InvoiceLine = NonNullable<HourDeficiencyInvoice["childBreakdown"]>[number]

const lineHours = (line: InvoiceLine): number =>
  line.billableDeficientHours ?? line.deficientHours ?? 0

const overlapsPeriod = (
  invoice: HourDeficiencyInvoice,
  periodStart: Date,
  periodEnd: Date,
): boolean => {
  const invoiceStart = toDate(invoice.periodStart)
  const invoiceEnd = toDate(invoice.periodEnd)
  return (
    !!invoiceStart &&
    !!invoiceEnd &&
    !(periodStart > invoiceEnd) &&
    !(invoiceStart > periodEnd)
  )
}

const assertUnambiguous = (
  invoiceId: string,
  matchingIds: string[],
  ambiguousInvoiceIds: Set<string>,
) => {
  if (matchingIds.length > 0 && ambiguousInvoiceIds.has(invoiceId)) {
    throw new AmbiguousLegacyInvoiceError(
      invoiceId,
      [...new Set(matchingIds)].sort(),
    )
  }
}

/** Hours of the requested entities' lines on a per-child invoice. */
const childLineHours = (
  invoice: HourDeficiencyInvoice & { childBreakdown: InvoiceLine[] },
  targetIds: Set<string>,
  ambiguousInvoiceIds: Set<string>,
): number[] => {
  const matching = invoice.childBreakdown.filter((line) =>
    targetIds.has(line.personId),
  )
  assertUnambiguous(
    invoice.id,
    matching.map((line) => line.personId),
    ambiguousInvoiceIds,
  )
  return matching.map(lineHours)
}

/** The whole invoice total when it bills any requested entity. */
const entityInvoiceHours = (
  invoice: InvoiceHourScope,
  targetIds: Set<string>,
  ambiguousInvoiceIds: Set<string>,
): number[] => {
  const invoiceEntityIds = [
    invoice.personId,
    ...(invoice.aggregatePersonIds ?? []),
    ...(invoice.aggregateUserIds ?? []),
    ...(invoice.requestedEntityIds ?? []),
    ...(invoice.recipientEntityIds ?? []),
  ]
  const matchingIds = invoiceEntityIds.filter(
    (entityId): entityId is string =>
      typeof entityId === "string" && targetIds.has(entityId),
  )
  assertUnambiguous(invoice.id, matchingIds, ambiguousInvoiceIds)
  return matchingIds.length > 0 ? [invoiceTotalHours(invoice)] : []
}

/**
 * Amounts one invoice deducts, in the order they are added to the total.
 * A family invoice counts once for its own family; others count per entity.
 */
const deductedHours = (
  invoice: InvoiceHourScope,
  targetIds: Set<string>,
  ambiguousInvoiceIds: Set<string>,
  familyKey: string | undefined,
): number[] => {
  if (familyKey && invoice.familyKey) {
    if (invoice.familyKey !== familyKey) return []
    return [
      invoice.childBreakdown?.length
        ? invoice.childBreakdown.reduce((sum, line) => sum + lineHours(line), 0)
        : invoiceTotalHours(invoice),
    ]
  }
  if (invoice.childBreakdown?.length) {
    return childLineHours(
      { ...invoice, childBreakdown: invoice.childBreakdown },
      targetIds,
      ambiguousInvoiceIds,
    )
  }
  return entityInvoiceHours(invoice, targetIds, ambiguousInvoiceIds)
}

export const calculateAlreadyInvoicedHours = (
  invoices: HourDeficiencyInvoice[],
  entityIds: Iterable<string>,
  periodStart: Date,
  periodEnd: Date,
  familyKey?: string,
  ambiguousLegacyInvoiceIds: Iterable<string> = [],
): number => {
  const targetIds = new Set(entityIds)
  const ambiguousInvoiceIds = new Set(ambiguousLegacyInvoiceIds)
  if (targetIds.size === 0) return 0

  let total = 0
  for (const invoice of invoices) {
    if (!deductionStatuses.has(invoice.status)) continue
    if (!overlapsPeriod(invoice, periodStart, periodEnd)) continue
    const amounts = deductedHours(
      invoice as InvoiceHourScope,
      targetIds,
      ambiguousInvoiceIds,
      familyKey,
    )
    // Add one amount at a time, as before, so float rounding is unchanged.
    for (const hours of amounts) total += hours
  }

  return Math.round(total * 100) / 100
}

/** Subscribes to invoices that deduct hours from person and user summaries. */
export const subscribeInvoicesForHourSummaries = (
  db: FirestoreInstance,
  callback: (invoices: HourDeficiencyInvoice[]) => void,
  onError?: (error: Error) => void,
): (() => void) => {
  const invoicesQuery = query(
    getInvoicesCollectionRef(db),
    where("status", "in", [...INVOICE_HOUR_DEDUCTION_STATUSES]),
  )

  return onSnapshot(
    invoicesQuery,
    (snapshot) => {
      callback(
        snapshot.docs.map(
          (document) =>
            ({
              id: document.id,
              ...document.data(),
            }) as HourDeficiencyInvoice,
        ),
      )
    },
    onError,
  )
}
